/* eslint-disable import/no-named-as-default */
import React from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";
import { useLocation, useHistory } from "react-router-dom";

import TopBar from "../layout/TopBar";
import NavSidebar from "../layout/NavSidebar";
import GlobalPlayerSearch from "../players/GlobalPlayerSearch.jsx";
import { PageContainer } from "../layout/PageContainer";
import MainSectionShell from "../layout/MainSectionShell";
import {
    APP_SHELL_TOP_OFFSET_SX,
    MAIN_SECTION_MIN_HEIGHT_SX,
    MOBILE_NAV_BOTTOM_INSET,
    MOBILE_NAV_FAB_BOTTOM,
    MOBILE_NAV_MEDIA,
    RIGHT_PANEL_TRANSITION,
} from "../layout/layoutConstants";
import { GoogleOAuthProvider, googleLogout } from "@react-oauth/google";
import CookieConsentBanner from "./CookieConsentBanner";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { Delete } from "@mui/icons-material";
import { MESSAGE_TYPES, useWebSocketStore } from "../../stores/websocketStore";
import JoinPartyDialog from "../layout/JoinPartyDialog.jsx";
import InviteDialog from "../lobby/InviteDialog.jsx";
import { buildInviteDataFromMessage } from "../lobby/inviteDialogUtils";
import FriendlistSidebar from "../layout/FriendlistSidebar";
import { GlobalLobbySettingsDrawer } from "../lobby-settings/LobbySettingsDrawer";
import ConfirmDialog from "../common/ConfirmDialog.jsx";
import Toast from "../common/Toast.jsx";
import { flushPendingToast } from "../../stores/toastStore";
import BackdropOverlay from "../common/BackdropOverlay.jsx";
import MatchReadyNotification from "./MatchReadyNotification.jsx";
import useDismissExpiredMatchAccept from "../../hooks/useDismissExpiredMatchAccept";
import {
    AuthStateContext,
    STEAM_SIGN_IN_URL,
    PLAYSAFE_SIGN_IN_URL,
} from "./AuthStateContext";
import UiDesignLaneGuard from "./UiDesignLaneGuard";
import PublicHeader from "../public-site/PublicHeader";
import PublicFooter from "../public-site/PublicFooter";
import TwoFactorVerify from "../auth/TwoFactorVerify";
import PlaySafeOnboardingDialog from "../auth/PlaySafeOnboardingDialog";
import { isPlaySafeSignupUser } from "../lobby/lobbyAccountStatusNotice";
import RefragPromoNotice from "../common/RefragPromoNotice";
import {
    CANONICAL_BASE_URL,
    OG_IMAGE_URL,
    getPublicPageMeta,
    isMarketingRoute,
} from "../public-site/siteData";
import {
    REFERRAL_CODE_PATTERN,
    REFERRAL_STORAGE_KEY,
} from "../../universal/referralLinks";
import {
    clearExplicitLogout,
    hasExplicitLogout,
    markExplicitLogout,
} from "../../universal/explicitLogoutGuard";
import {
    isAbortError,
    restoreSessionWithRetry,
} from "../../universal/sessionRestore";
import { getOrCreateVisitorId } from "../../universal/track";
import { trackRedditGoogleSignup } from "../../universal/redditPixel";
import { isLobbyV2Path, isV2Path, toLobbyPath } from "../../universal/v2Routes";
import {
    captureFromSearch,
    getStoredAttribution,
    clearStoredAttribution,
} from "../../universal/campaignAttribution";
import { TemplateTrackingProvider } from "../templates/TemplateTrackingProvider";
import { useTemplateConfig } from "../templates/useTemplateConfig";
import {
    getTemplateFooterComponent,
    getTemplateHeaderComponent,
} from "../templates/registry";
import { shouldHidePublicHeaderSpacer } from "../templates/publicHeaderLayout";
import { colors } from "../../theme/colors";
import { AnimatePresence, motion } from "framer-motion";
import PersistentQueueStatus, {
    shouldShowPersistentQueue,
} from "../layout/PersistentQueueStatus";
import PersistentMapVoteStatus, {
    shouldShowPersistentMapVote,
} from "../layout/PersistentMapVoteStatus";
import {
    armMatchPageLandedSoundForMatch,
    playMatchAcceptSound,
    playMatchFoundSoundOnce,
    clearMatchFoundSoundOnce,
    syncMatchPageLandedSoundMuteFromUser,
} from "../../universal/matchNotificationSounds";
import { toast } from "../../universal/toast";
import {
    getMatchServerReadyMatchId,
    isAlreadyOnMatchRoute,
} from "../lobby/lobbyPageUtils";
import useGlobalFriendRequests from "./useGlobalFriendRequests";
import {
    LOBBY_REVIEW_PANEL_ANCHOR_ID,
    LobbyReviewPanelBridgeProvider,
} from "../lobby/LobbyReviewPanelBridgeContext";

const MotionBox = motion(Box);

const basePath =
    process.env.NODE_ENV === "production" ? "/edge" : "http://localhost:3010";

const PLAYSAFE_EMAIL_PROMPT_DISMISSED_KEY = "ouro_playsafe_email_prompt_dismissed";

function PublicTemplateShell({ children, footer, headerProps, initialTemplate, spacer }) {
    const { codename, isResolved } = useTemplateConfig(initialTemplate);
    const location = useLocation();
    const isFocusedAuthStatusPage =
        location.pathname === "/auth/playsafe/status" ||
        location.pathname === "/auth/playsafe/connect-steam" ||
        location.pathname === "/auth/steam/status";
    const hidePublicHeader =
        isFocusedAuthStatusPage || (location.pathname === "/" && codename === "original");
    const hidePublicHeaderSpacer = shouldHidePublicHeaderSpacer(location.pathname, codename);
    const Header = getTemplateHeaderComponent(codename) || PublicHeader;
    const TemplateFooter = getTemplateFooterComponent(codename);

    return (
        <TemplateTrackingProvider codename={codename} isResolved={isResolved}>
            {hidePublicHeader ? null : <Header {...headerProps} />}
            {hidePublicHeader || hidePublicHeaderSpacer ? null : spacer}
            {children}
            {isFocusedAuthStatusPage ? null : TemplateFooter ? <TemplateFooter /> : footer}
        </TemplateTrackingProvider>
    );
}

PublicTemplateShell.propTypes = {
    children: PropTypes.node,
    footer: PropTypes.node,
    headerProps: PropTypes.shape({
        isAuthenticated: PropTypes.bool,
        googleResponse: PropTypes.func,
        onFailure: PropTypes.func,
    }),
    initialTemplate: PropTypes.shape({
        codename: PropTypes.string,
        isResolved: PropTypes.bool,
    }),
    spacer: PropTypes.node,
};

function clearLegacyClientAuth() {
    if (typeof window === "undefined") {
        return;
    }

    try {
        window.localStorage.removeItem("ouroAuthToken");
        window.localStorage.removeItem("ouroGoogleToken");
    } catch (error) {
        // Best-effort cleanup only.
    }

    if (typeof document !== "undefined") {
        document.cookie = "ouro-jwt=; Max-Age=0; path=/";
    }
}

export function createAppCleanupHandlers({
    clearLocalAuthState,
    clearLegacyClientAuth: clearLegacyClientAuthImpl = clearLegacyClientAuth,
    googleLogoutFn = googleLogout,
    logoutAction = () => { },
} = {}) {
    const applyBaseCleanup = () => {
        clearLocalAuthState?.();
        clearLegacyClientAuthImpl?.();
    };

    const applyAuthFailureState = () => {
        applyBaseCleanup();
        logoutAction?.();
    };

    const applyLoggedOutState = () => {
        applyBaseCleanup();
        try {
            googleLogoutFn?.();
        } catch (error) {
            // Best-effort cleanup only.
        }
        logoutAction?.();
    };

    return {
        applyAuthFailureState,
        applyLoggedOutState,
    };
}

export function createExplicitLogoutController(applyLoggedOutState) {
    let applied = false;

    return {
        apply() {
            if (applied) {
                return false;
            }
            applied = true;
            applyLoggedOutState?.();
            return true;
        },
        reset() {
            applied = false;
        },
    };
}

const SESSION_RESTORE_FETCH_OPTIONS = {
    method: "GET",
    credentials: "include",
    mode: "cors",
    cache: "no-store",
};

function App({ catalog, actions, children }) {
    const location = useLocation();
    const history = useHistory();
    const isMobileNav = useMediaQuery(MOBILE_NAV_MEDIA);
    const [state, setState] = React.useState({
        isAuthenticated: false,
        hasStoredToken: true,
        user: null,
        showRefreshWindow: false,
        autoLoginInProgress: true,
        googleAuthFailure: null,
        twoFactorPending: false,
        twoFactorToken: null,
        sessionRestoreFailed: false,
    });
    const [globalPlayerSearchOpen, setGlobalPlayerSearchOpen] = React.useState(false);
    const [playSafeEmailPromptDismissed, setPlaySafeEmailPromptDismissed] =
        React.useState(() => {
            if (typeof window === "undefined" || !window.localStorage) return false;
            try {
                return (
                    window.localStorage.getItem(PLAYSAFE_EMAIL_PROMPT_DISMISSED_KEY) === "1"
                );
            } catch {
                return false;
            }
        });

    const { connect, disconnect, performLogout, subscribe, sendOwnerAction, clientId } =
        useWebSocketStore();
    const matchAcceptDialogStateRef = React.useRef(null);
    const matchAcceptanceCountRef = React.useRef(new Map());
    const catalogRef = React.useRef(catalog);
    const logoutInFlightRef = React.useRef(false);
    const sessionAbortRef = React.useRef(null);
    const authGenerationRef = React.useRef(0);
    const locationPathnameRef = React.useRef(location.pathname);

    React.useEffect(() => {
        syncMatchPageLandedSoundMuteFromUser(state.user || catalog?.user);
    }, [state.user, catalog?.user]);

    React.useEffect(() => {
        locationPathnameRef.current = location.pathname;
    }, [location.pathname]);

    const clearLocalAuthState = React.useCallback(() => {
        setState((prev) => ({
            ...prev,
            isAuthenticated: false,
            hasStoredToken: false,
            user: null,
            autoLoginInProgress: false,
            googleAuthFailure: "Authentication failed",
        }));
    }, []);

    const { applyAuthFailureState, applyLoggedOutState } = React.useMemo(
        () =>
            createAppCleanupHandlers({
                clearLocalAuthState,
                clearLegacyClientAuth,
                googleLogoutFn: googleLogout,
                logoutAction: actions.logout,
            }),
        [actions.logout, clearLocalAuthState]
    );
    const explicitLogoutController = React.useMemo(
        () => createExplicitLogoutController(applyLoggedOutState),
        [applyLoggedOutState]
    );

    const onFailure = React.useCallback(() => {
        applyAuthFailureState();
    }, [applyAuthFailureState]);

    const restoreSession = React.useCallback(
        () => {
            if (hasExplicitLogout()) {
                setState((prev) => ({
                    ...prev,
                    isAuthenticated: false,
                    hasStoredToken: false,
                    user: null,
                    autoLoginInProgress: false,
                    sessionRestoreFailed: false,
                }));
                return;
            }

            sessionAbortRef.current?.abort();
            const controller = new AbortController();
            sessionAbortRef.current = controller;
            const generation = authGenerationRef.current;
            const isCancelled = () =>
                generation !== authGenerationRef.current || hasExplicitLogout();

            setState((prev) => ({
                ...prev,
                hasStoredToken: true,
                autoLoginInProgress: true,
                sessionRestoreFailed: false,
                googleAuthFailure: null,
            }));

            restoreSessionWithRetry({
                url: `${basePath}/auth/session`,
                fetchOptions: {
                    ...SESSION_RESTORE_FETCH_OPTIONS,
                    signal: controller.signal,
                },
                isCancelled,
            })
                .then((result) => {
                    if (isCancelled()) {
                        return;
                    }

                    if (result.type === "aborted") {
                        return;
                    }

                    if (result.type === "success") {
                        const user = result.user;

                        clearStoredAttribution();

                        actions.setUser({ user });
                        setState((prev) => ({
                            ...prev,
                            isAuthenticated: true,
                            hasStoredToken: true,
                            user,
                            autoLoginInProgress: false,
                            sessionRestoreFailed: false,
                            googleAuthFailure: null,
                        }));
                        return;
                    }

                    if (result.type === "unauthenticated") {
                        onFailure();
                        return;
                    }

                    setState((prev) => ({
                        ...prev,
                        isAuthenticated: false,
                        hasStoredToken: true,
                        autoLoginInProgress: false,
                        sessionRestoreFailed: true,
                    }));
                })
                .catch((error) => {
                    if (isAbortError(error) || isCancelled()) {
                        return;
                    }
                    setState((prev) => ({
                        ...prev,
                        isAuthenticated: false,
                        hasStoredToken: true,
                        autoLoginInProgress: false,
                        sessionRestoreFailed: true,
                    }));
                });
        },
        [actions, onFailure]
    );

    const googleResponse = ({ credential }) => {
        clearExplicitLogout();
        const acquisition = getStoredAttribution();
        const options = {
            method: "POST",
            body: JSON.stringify({
                credential,
                surface: "hero",
                visitorId: getOrCreateVisitorId(),
                ...(acquisition ? { acquisition } : {}),
            }),
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            mode: "cors",
            cache: "default",
        };

        setState((prev) => ({
            ...prev,
            hasStoredToken: true,
            autoLoginInProgress: true,
            sessionRestoreFailed: false,
            googleAuthFailure: null,
        }));

        fetch(`${basePath}/auth/google`, options)
            .then((r) => {
                return r.json().then((resp) => {
                    if (resp?.twoFactorRequired) {
                        setState((prev) => ({
                            ...prev,
                            twoFactorPending: true,
                            twoFactorToken: resp.twoFactorToken,
                            autoLoginInProgress: false,
                            googleAuthFailure: null,
                        }));
                        return;
                    }

                    if (!r.ok || !resp?.user) {
                        throw new Error(resp?.message || "Authentication failed");
                    }

                    const user = resp.user;

                    clearStoredAttribution();
                    trackRedditGoogleSignup(resp.isNewUser === true, resp.redditConversionId);

                    actions.setUser({ user });
                    setState((prev) => ({
                        ...prev,
                        isAuthenticated: true,
                        hasStoredToken: true,
                        user,
                        autoLoginInProgress: false,
                        sessionRestoreFailed: false,
                        googleAuthFailure: null,
                        twoFactorPending: false,
                        twoFactorToken: null,
                    }));
                });
            })
            .catch(onFailure);
    };

    React.useEffect(() => {
        clearLegacyClientAuth();
        restoreSession();
        // Mount-only: `actions` is rebound when route children change, and a
        // second restore during logout races the still-valid session cookie.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        flushPendingToast();
    }, []);

    React.useEffect(() => {
        try {
            const params = new URLSearchParams(location.search);
            const refCode = params.get("ref");
            if (refCode && REFERRAL_CODE_PATTERN.test(refCode)) {
                window.localStorage.setItem(REFERRAL_STORAGE_KEY, refCode.toUpperCase());
            }
        } catch (_) {
            // Best-effort referral capture only.
        }
    }, [location.search]);

    React.useEffect(() => {
        try {
            captureFromSearch(location.search);
        } catch (_) {
            // Best-effort attribution capture only.
        }
    }, [location.search]);

    React.useEffect(() => {
        try {
            const params = new URLSearchParams(location.search);
            const twoFactorToken = params.get("twoFactorToken");
            if (!twoFactorToken) {
                return;
            }

            setState((prev) => ({
                ...prev,
                twoFactorPending: true,
                twoFactorToken,
                autoLoginInProgress: false,
                googleAuthFailure: null,
            }));

            params.delete("twoFactorToken");
            history.replace({
                pathname: location.pathname,
                search: params.toString() ? `?${params.toString()}` : "",
                hash: location.hash,
            });
        } catch (_) {
            // Best-effort deep-link handling only.
        }
    }, [history, location.hash, location.pathname, location.search]);

    React.useEffect(() => {
        if (state.isAuthenticated && state.user) {
            explicitLogoutController.reset();
            connect(state.user);
        } else {
            disconnect();
        }
    }, [state.isAuthenticated, state.user, connect, disconnect, explicitLogoutController]);

    const logout = React.useCallback(() => {
        markExplicitLogout();
        logoutInFlightRef.current = true;
        authGenerationRef.current += 1;
        sessionAbortRef.current?.abort();
        void performLogout({
            httpLogout: async () => {
                try {
                    await fetch(`${basePath}/auth/logout`, {
                        method: "POST",
                        credentials: "include",
                        mode: "cors",
                        cache: "no-cache",
                    });
                } catch (error) {
                    // Best-effort logout request only.
                }
            },
            clearLocalSession: async () => {
                logoutInFlightRef.current = false;
                explicitLogoutController.reset();
                explicitLogoutController.apply();
                history.push("/");
            },
        });
    }, [explicitLogoutController, performLogout, history]);

    const completeTwoFactor = React.useCallback(
        (user) => {
            clearExplicitLogout();
            actions.setUser({ user });
            setState((prev) => ({
                ...prev,
                isAuthenticated: true,
                hasStoredToken: true,
                user,
                autoLoginInProgress: false,
                twoFactorPending: false,
                twoFactorToken: null,
                googleAuthFailure: null,
                sessionRestoreFailed: false,
            }));
        },
        [actions]
    );

    const cancelTwoFactor = React.useCallback(() => {
        setState((prev) => ({
            ...prev,
            twoFactorPending: false,
            twoFactorToken: null,
            hasStoredToken: false,
            autoLoginInProgress: false,
        }));
    }, []);

    const dismissPlaySafeEmailPrompt = React.useCallback(() => {
        if (typeof window !== "undefined" && window.localStorage) {
            try {
                window.localStorage.setItem(PLAYSAFE_EMAIL_PROMPT_DISMISSED_KEY, "1");
            } catch {
                // localStorage may be unavailable
            }
        }
        setPlaySafeEmailPromptDismissed(true);
    }, []);

    const handlePlaySafeOnboardingComplete = React.useCallback(async () => {
        const refreshedUser = await actions.refreshSessionUser();
        setState((prev) => ({
            ...prev,
            user: refreshedUser || prev.user,
        }));
        dismissPlaySafeEmailPrompt();
    }, [actions, dismissPlaySafeEmailPrompt]);

    const handlePlaySafeOnboardingDismiss = React.useCallback(() => {
        dismissPlaySafeEmailPrompt();
    }, [dismissPlaySafeEmailPrompt]);

    const {
        isAuthenticated,
        hasStoredToken,
        user,
        autoLoginInProgress,
        twoFactorPending,
        sessionRestoreFailed,
    } = state;
    const {
        performLogout: shouldPerformLogout,
        joinLobbyDialogOpened = false,
        lobbyCodeNotFound = false,
        lobbyFull = false,
        matchAcceptDialogState = false,
        mapVoteState = false,
        inviteData = null,
        inviteCountdown = 60000,
        inviteDialogOpen = false,
        lobbyState = "standby",
        matchmakingStarted = false,
        queueStatus = null,
    } = catalog;

    const catalogUser = catalog?.user;
    const resolvedUserSteamId =
        user?.steamId ||
        user?.steamId64 ||
        user?.steam64Id ||
        catalogUser?.steamId ||
        catalogUser?.steamId64 ||
        catalogUser?.steam64Id ||
        null;
    const resolvedUserSteamIdRef = React.useRef(resolvedUserSteamId);

    React.useEffect(() => {
        resolvedUserSteamIdRef.current = resolvedUserSteamId;
    }, [resolvedUserSteamId]);

    const joinLobbyByCode = React.useCallback(
        (lobbyCode) => {
            if (!user?.userId) return;
            const path =
                (location.pathname || "/").replace(/\/+$/, "") || "/";
            const lobbyPath = toLobbyPath(location.pathname);
            if (path !== lobbyPath) {
                history.push(lobbyPath);
            }
            sendOwnerAction({
                messageId: "join-lobby-by-code",
                lobbyCode,
                userId: user.userId,
            });
        },
        [sendOwnerAction, user?.userId, history, location.pathname]
    );

    const handleCancelJoinLobbyDialog = React.useCallback(() => {
        actions.closeJoinLobbyDialog();
    }, [actions]);

    const onAcceptMatch = React.useCallback(() => {
        if (!matchAcceptDialogState?.matchId) return;
        actions.lobbyAcceptMatch({ matchId: matchAcceptDialogState.matchId });
        sendOwnerAction({
            messageId: "accept-match",
            matchId: matchAcceptDialogState.matchId,
            userId: user?.userId,
            clientId,
        });
        if (process.env.NODE_ENV !== "production") {
            console.log("accepting match", matchAcceptDialogState.matchId);
        }
    }, [actions, clientId, matchAcceptDialogState, sendOwnerAction, user?.userId]);

    React.useEffect(() => {
        matchAcceptDialogStateRef.current = matchAcceptDialogState;
    }, [matchAcceptDialogState]);

    const onAcceptDeadlineExpired = React.useCallback(
        ({ matchId }) => {
            actions.matchAcceptanceCancelled({
                matchId,
                outcome: "returned-to-lobby",
                reason: "accept-timeout",
            });
        },
        [actions]
    );
    useDismissExpiredMatchAccept({
        matchAcceptDialogState,
        onExpired: onAcceptDeadlineExpired,
    });

    React.useEffect(() => {
        catalogRef.current = catalog;
    }, [catalog]);

    React.useEffect(() => {
        const unsubscribe = subscribe(({ message }) => {
            if (!message?.messageId) return;

            if (message.messageId === MESSAGE_TYPES.SESSION_ENDED) {
                if (!logoutInFlightRef.current) {
                    markExplicitLogout();
                    explicitLogoutController.apply();
                }
                return;
            }

            switch (message.messageId) {
                case MESSAGE_TYPES.HANDSHAKE_COMPLETED:
                case MESSAGE_TYPES.DISPLAY_STATUS_UPDATED:
                    if (message.displayPresence) {
                        actions.updateDisplayPresence(message.displayPresence);
                    }
                    break;

                case MESSAGE_TYPES.DISPLAY_STATUS_ERROR:
                    toast.error(
                        message.error || "Could not update display status",
                    );
                    break;

                case MESSAGE_TYPES.MATCHMAKING_STARTED_NOTIFICATION: {
                    const currentCatalog = catalogRef.current || {};
                    const alreadySearching =
                        currentCatalog.lobbyState === "searching" ||
                        Boolean(currentCatalog.matchmakingStarted);
                    actions.notifyMatchmakingStarted({
                        playerQueueActionStartTs:
                            message.playerQueueActionStartTs,
                    });
                    if (
                        !alreadySearching &&
                        message.leaderUserId &&
                        user?.userId &&
                        String(message.leaderUserId) !== String(user.userId)
                    ) {
                        toast.info("Your party leader started matchmaking", {
                            durationMs: 4500,
                        });
                    }
                    break;
                }

                case MESSAGE_TYPES.MATCHMAKING_STOPPED_NOTIFICATION: {
                    const currentCatalog = catalogRef.current || {};
                    const wasSearching =
                        currentCatalog.lobbyState === "searching" ||
                        Boolean(currentCatalog.matchmakingStarted);
                    actions.resetMatchmakingStarted();
                    actions.clearQueueStatus();
                    if (
                        wasSearching &&
                        message.leaderUserId &&
                        user?.userId &&
                        String(message.leaderUserId) !== String(user.userId)
                    ) {
                        toast.info("Your party leader stopped matchmaking", {
                            durationMs: 4500,
                        });
                    }
                    break;
                }

                case MESSAGE_TYPES.QUEUE_STATUS_UPDATE: {
                    const currentCatalog = catalogRef.current || {};
                    actions.updateQueueStatus({
                        playersInQueue: message.playersInQueue,
                        playersNeeded: message.playersNeeded,
                        algorithmEnabled: message.algorithmEnabled,
                        hint: message.hint,
                        estimatedWaitSeconds: message.estimatedWaitSeconds,
                        geoQueue: message.geoQueue,
                        smallerMatchOffer: message.smallerMatchOffer,
                    });
                    if (
                        currentCatalog.currentLobbyCode &&
                        Array.isArray(currentCatalog.currentLobby?.members) &&
                        currentCatalog.currentLobby.members.length > 1 &&
                        currentCatalog.lobbyState !== "searching" &&
                        !currentCatalog.matchmakingStarted
                    ) {
                        actions.notifyMatchmakingStarted();
                    }
                    break;
                }

                case MESSAGE_TYPES.MATCH_FOUND_NOTIFICATION:
                    playMatchFoundSoundOnce(message.matchId);
                    actions.showMatchFoundNotification(
                        message.matchId,
                        message.timeoutSeconds,
                        message.map,
                        message.totalPlayers,
                        {
                            expiresAt: message.expiresAt,
                            hasAccepted: message.hasAccepted,
                            acceptedCount: message.acceptedCount,
                            allAccepted: message.allAccepted,
                            slots: message.slots,
                            partyAcceptedUserIds: message.partyAcceptedUserIds,
                            location: message.location || null,
                            mapVotePending: message.mapVotePending,
                            teams: message.teams,
                        }
                    );
                    actions.clearQueueStatus();
                    break;

                case MESSAGE_TYPES.MATCH_ACCEPTANCE_UPDATE: {
                    try {
                        const matchId = message.matchId;
                        const nextAcceptedCount = Number(
                            message.acceptedCount ?? 0
                        );
                        const prevAcceptedCount =
                            matchAcceptanceCountRef.current.get(matchId) ?? 0;

                        matchAcceptanceCountRef.current.set(
                            matchId,
                            nextAcceptedCount
                        );

                        const activeMatchId =
                            matchAcceptDialogStateRef.current?.matchId;
                        if (
                            activeMatchId &&
                            activeMatchId === matchId &&
                            nextAcceptedCount > prevAcceptedCount
                        ) {
                            playMatchAcceptSound();
                        }
                    } catch (error) {
                        // Never let sound issues break the handler.
                    }

                    actions.updateMatchAcceptanceStatus({
                        matchId: message.matchId,
                        acceptedCount: message.acceptedCount,
                        totalPlayers: message.totalPlayers,
                        allAccepted: message.allAccepted,
                        hasAccepted: message.hasAccepted,
                        expiresAt: message.expiresAt,
                        slots: message.slots,
                        partyAcceptedUserIds: message.partyAcceptedUserIds,
                        mapVotePending: message.mapVotePending,
                        map: message.map,
                        teams: message.teams,
                    });
                    break;
                }

                case MESSAGE_TYPES.MATCH_ACCEPTANCE_CANCELLED: {
                    clearMatchFoundSoundOnce(message.matchId);
                    const cancelledMatchId =
                        message.matchId != null && message.matchId !== ""
                            ? String(message.matchId)
                            : null;
                    const activeMatchId =
                        matchAcceptDialogStateRef.current?.matchId != null
                            ? String(matchAcceptDialogStateRef.current.matchId)
                            : null;
                    const wasLiveAcceptPrompt =
                        Boolean(cancelledMatchId) &&
                        Boolean(activeMatchId) &&
                        cancelledMatchId === activeMatchId;

                    actions.matchAcceptanceCancelled({
                        matchId: message.matchId,
                        outcome: message.outcome,
                        reason: message.reason,
                        playerQueueActionStartTs:
                            message.playerQueueActionStartTs,
                    });

                    // Only for the live accept window (not handshake replay).
                    if (
                        wasLiveAcceptPrompt &&
                        message.outcome === "requeued"
                    ) {
                        toast.warning(
                            "A player didn't accept — searching again",
                            { durationMs: 5000 },
                        );
                    }
                    break;
                }

                case MESSAGE_TYPES.MAP_VOTE_STARTED:
                    actions.startMapVote({
                        matchId: message.matchId,
                        candidates: message.candidates,
                        blockedMaps: message.blockedMaps,
                        expiresAt: message.expiresAt,
                        durationSeconds: message.durationSeconds,
                        voters: message.voters,
                        votes: message.votes,
                        myVote: message.myVote,
                        teamStats: message.teamStats,
                    });
                    break;

                case MESSAGE_TYPES.MAP_VOTE_UPDATE:
                    actions.updateMapVote({
                        matchId: message.matchId,
                        candidates: message.candidates,
                        blockedMaps: message.blockedMaps,
                        expiresAt: message.expiresAt,
                        durationSeconds: message.durationSeconds,
                        voters: message.voters,
                        votes: message.votes,
                        myVote: message.myVote,
                        teamStats: message.teamStats,
                    });
                    break;

                case MESSAGE_TYPES.MAP_VOTE_RESOLVED:
                    actions.resolveMapVote({
                        matchId: message.matchId,
                        map: message.map,
                        tally: message.tally,
                        votes: message.votes,
                        voters: message.voters,
                        teamStats: message.teamStats,
                        wasTie: message.wasTie,
                        tieBrokenBy: message.tieBrokenBy,
                        tiedMaps: message.tiedMaps,
                        resolvedBy: message.resolvedBy,
                        topCount: message.topCount,
                    });
                    break;

                case MESSAGE_TYPES.MATCHMAKING_STARTED_NOTIFICATION:
                    actions.notifyMatchmakingStarted({
                        playerQueueActionStartTs:
                            message.playerQueueActionStartTs,
                    });
                    break;

                case MESSAGE_TYPES.MATCHMAKING_STOPPED_NOTIFICATION:
                    actions.resetMatchmakingStarted();
                    actions.clearQueueStatus();
                    break;

                case MESSAGE_TYPES.QUEUE_STATUS_UPDATE:
                    actions.updateQueueStatus({
                        playersInQueue: message.playersInQueue,
                        playersNeeded: message.playersNeeded,
                        algorithmEnabled: message.algorithmEnabled,
                        hint: message.hint,
                        estimatedWaitSeconds: message.estimatedWaitSeconds,
                        smallerMatchOffer: message.smallerMatchOffer,
                    });
                    if (
                        catalog?.currentLobbyCode &&
                        Array.isArray(catalog?.currentLobby?.members) &&
                        catalog.currentLobby.members.length > 1 &&
                        catalog.lobbyState !== "searching" &&
                        !catalog.matchmakingStarted
                    ) {
                        actions.notifyMatchmakingStarted();
                    }
                    break;

                case MESSAGE_TYPES.MATCH_SERVER_READY: {
                    const readyMatchId = getMatchServerReadyMatchId(message);
                    clearMatchFoundSoundOnce(readyMatchId);
                    actions.lobbyStopSearch({ userId: user?.userId });
                    actions.closeMatchAcceptDialog();
                    actions.closeMapVote();

                    // Handshake/reconnect replays this event. Clearing match data while
                    // already on this match page stops polling and leaves the overlay up.
                    if (
                        isAlreadyOnMatchRoute(
                            locationPathnameRef.current,
                            readyMatchId,
                        )
                    ) {
                        break;
                    }

                    actions.clearMatchData();

                    if (readyMatchId) {
                        armMatchPageLandedSoundForMatch(readyMatchId);
                        history.push(
                            message.redirectTo || `/match/${readyMatchId}`,
                        );
                    } else if (message.redirectTo) {
                        history.push(message.redirectTo);
                    }
                    break;
                }

                case MESSAGE_TYPES.FRIENDS_BOOTSTRAP:
                case MESSAGE_TYPES.FRIENDS_LIST:
                    actions.setFriendsFromBootstrap(message.friends);
                    break;

                case MESSAGE_TYPES.INVITE_RECEIVED:
                    actions.startInvite({
                        inviteData: buildInviteDataFromMessage(
                            message,
                            catalogRef.current?.friends || [],
                        ),
                    });
                    break;

                case MESSAGE_TYPES.INVITE_EXPIRED:
                    actions.inviteExpired(message.toUserId);
                    break;

                case MESSAGE_TYPES.INVITE_DECLINED:
                    actions.inviteDeclined(message.toUserId);
                    break;

                case MESSAGE_TYPES.INVITE_CANCELLED: {
                    const myUserId =
                        catalogRef.current?.user?.userId || user?.userId;
                    const isSender =
                        myUserId &&
                        String(message.fromUserId) === String(myUserId);
                    actions.inviteCancelled({
                        guestId: message.toUserId,
                        inviteId: message.inviteId,
                        fromUserId: message.fromUserId,
                    });
                    if (isSender) {
                        toast.info("Invitation cancelled");
                    } else {
                        toast.info(
                            `${message.cancelledByNickname || "A player"} cancelled the invitation`,
                        );
                    }
                    break;
                }

                case MESSAGE_TYPES.INVITE_ACCEPTED:
                    actions.inviteAccepted(message.guestId);
                    break;

                case MESSAGE_TYPES.INVITE_ERROR:
                    toast.error(
                        message.message ||
                            message.error ||
                            "Unable to send or accept the lobby invite.",
                    );
                    break;

                case MESSAGE_TYPES.INVITE_REJECTED_FULL:
                    toast.error(
                        "The invite could not be accepted because your lobby is full.",
                    );
                    break;

                case MESSAGE_TYPES.FRIEND_REQUESTS_BOOTSTRAP:
                    actions.setFriendRequestsFromBootstrap(message.friendRequests);
                    break;

                case MESSAGE_TYPES.FRIEND_REQUEST_RECEIVED:
                    actions.addFriendRequestLocally(
                        message.fromUserSteamId,
                        message.toUserSteamId,
                    );
                    if (resolvedUserSteamIdRef.current) {
                        actions.initFriendRequests(resolvedUserSteamIdRef.current);
                    }
                    break;

                default:
                    break;
            }
        });

        return unsubscribe;
    }, [actions, catalog, explicitLogoutController, history, subscribe, user?.userId]);

    const baseDescription =
        "Ouro is a trust-focused Counter-Strike matchmaking platform built around verified players and better match quality.";
    const pathname = location?.pathname || "/";
    const showPersistentMapVote =
        isAuthenticated && shouldShowPersistentMapVote(mapVoteState, pathname);
    const showPersistentQueue =
        isAuthenticated &&
        !matchAcceptDialogState?.matchId &&
        !showPersistentMapVote &&
        shouldShowPersistentQueue(catalog, pathname);
    const isPublicShellRoute = isMarketingRoute(pathname);
    const isActiveLobbyPath = isLobbyV2Path(pathname) || pathname === "/lobby";
    const publicPageMeta = getPublicPageMeta(pathname);
    const normalizedAccountStatus =
        typeof user?.accountStatus === "string"
            ? user.accountStatus.trim().toLowerCase()
            : null;
    const showPlaySafeOnboarding =
        !twoFactorPending &&
        isAuthenticated &&
        Boolean(user) &&
        isPlaySafeSignupUser(user) &&
        user.emailIsSynthetic === true &&
        !playSafeEmailPromptDismissed;
    const shouldShowRefragPromo =
        !twoFactorPending &&
        isAuthenticated &&
        Boolean(user) &&
        normalizedAccountStatus === "verified";
    const showRefragPromoLegacy = shouldShowRefragPromo && !isV2Path(pathname);

    // Mandatory gate: PlaySafe identities are never treated as verified until a
    // steamId64 is confirmed. Redirect away from anything but the Connect
    // Steam / status screens whenever the session lands in this state (e.g. a
    // stale tab, a direct lobby link, or a refresh mid-flow).
    React.useEffect(() => {
        if (twoFactorPending || !isAuthenticated || !user) return;
        if (normalizedAccountStatus !== "pending_steam_link") return;
        if (
            pathname === "/auth/playsafe/connect-steam" ||
            pathname === "/auth/playsafe/status"
        ) {
            return;
        }
        history.replace("/auth/playsafe/connect-steam");
    }, [twoFactorPending, isAuthenticated, user, normalizedAccountStatus, pathname, history]);

    const authContextValue = React.useMemo(
        () => ({
            isAuthenticated,
            hasStoredToken,
            autoLoginInProgress,
            twoFactorPending,
            performLogout: shouldPerformLogout,
            sessionRestoreFailed,
            retrySessionRestore: restoreSession,
            googleResponse,
            onFailure,
            steamSignInUrl: STEAM_SIGN_IN_URL,
            playsafeSignInUrl: PLAYSAFE_SIGN_IN_URL,
            user,
            logout,
        }),
        [
            isAuthenticated,
            hasStoredToken,
            autoLoginInProgress,
            twoFactorPending,
            shouldPerformLogout,
            sessionRestoreFailed,
            restoreSession,
            googleResponse,
            onFailure,
            user,
            logout,
        ]
    );

    const friendRequests = catalog?.friendRequests ?? [];
    const friends = catalog?.friends ?? [];
    const friendToDelete = catalog?.friendToDelete ?? null;
    const inviteState = catalog?.inviteState ?? {};

    useGlobalFriendRequests({
        actions,
        isAuthenticated,
        user,
        catalogUser,
    });

    const isLobbyFull = React.useMemo(() => {
        const data =
            catalog?.currentLobbyCode && catalog?.currentLobby
                ? catalog.currentLobby
                : null;
        if (!data) return false;
        return (
            (data.members?.length ?? 0) >= (data.maxPlayers ?? 0) || false
        );
    }, [catalog?.currentLobbyCode, catalog?.currentLobby]);

    const handleAcceptFriendRequest = React.useCallback(
        (fromUserSteamId, toUserSteamId) =>
            actions.acceptFriendRequest({
                fromUserSteamId,
                toUserSteamId,
                toUserId: catalogUser?.userId,
            }),
        [actions, catalogUser?.userId]
    );

    const handleFriendRequestToCancel = React.useCallback(
        (friendRequestId) => {
            actions.cancelFriendRequest({ friendRequestId });
        },
        [actions]
    );

    const handleDeclineFriendRequest = React.useCallback(
        (fromUserSteamId, toUserSteamId) =>
            actions.declineFriendRequest({
                fromUserSteamId,
                toUserSteamId,
                toUserId: catalogUser?.userId,
            }),
        [actions, catalogUser?.userId]
    );

    const handleDeleteFriend = React.useCallback(
        (friendSteamId) => {
            const friend = friends.find((f) => f.friendSteamId === friendSteamId);
            actions.openConfirmDialog({ which: "deleteFriend", friend });
        },
        [actions, friends]
    );

    const handleConfirmDeleteFriend = React.useCallback(() => {
        if (!friendToDelete || !catalogUser?.userId) return;
        if (!resolvedUserSteamId) return;

        const friendSteamId = friendToDelete.friendSteamId;
        actions.closeConfirmDialog();
        actions.removeFriendLocally(friendSteamId);
        actions.removePendingRequestLocally(friendSteamId);

        actions.deleteFriend({
            mySteamId: resolvedUserSteamId,
            friendSteamId,
            userId: catalogUser.userId,
        });
    }, [actions, catalogUser?.userId, friendToDelete, resolvedUserSteamId]);

    const handleCloseGlobalConfirmDialog = React.useCallback(() => {
        actions.closeConfirmDialog();
    }, [actions]);

    const handleSendInvite = React.useCallback(
        (friend) => {
            if (!resolvedUserSteamId || !catalogUser?.userId) return;

            sendOwnerAction({
                messageId: "invite-send",
                fromUserId: catalogUser.userId,
                fromUserSteamId: resolvedUserSteamId,
                toUserId: friend.friendData.userId,
            });

            actions.inviteSent(friend.friendData.userId);
        },
        [actions, catalogUser?.userId, resolvedUserSteamId, sendOwnerAction]
    );

    const handleClearInviteError = React.useCallback(
        (toUserId) => {
            actions.clearInviteError(toUserId);
        },
        [actions]
    );

    const handleInviteDeclined = React.useCallback(
        (inviteId) => {
            sendOwnerAction({
                messageId: "invite-decline",
                inviteId,
            });
        },
        [sendOwnerAction]
    );

    const handleInviteAccepted = React.useCallback(
        (inviteId, incomingInvite) => {
            const toUserId = catalogUser?.userId || user?.userId;
            if (!toUserId) return;
            sendOwnerAction({
                messageId: "invite-accept",
                inviteId,
                fromUserId: incomingInvite.fromUserId,
                toUserId,
            });
        },
        [catalogUser?.userId, sendOwnerAction, user?.userId]
    );

    const handleCancelPersistentQueue = React.useCallback(() => {
        const userId = catalogUser?.userId || user?.userId;
        if (!userId) return;

        actions.lobbyStopSearch({ userId });
        actions.clearQueueStatus();

        const currentLobbyCode = catalog?.currentLobbyCode;
        const members = Array.isArray(catalog?.currentLobby?.members)
            ? catalog.currentLobby.members
            : [];
        if (currentLobbyCode && members.length > 0) {
            sendOwnerAction({
                messageId: "matchmaking-stopped",
                lobbyCode: currentLobbyCode,
                leaderUserId: userId,
                memberUserIds: members
                    .filter((member) => member.userId !== userId)
                    .map((member) => member.userId),
            });
        }
    }, [
        actions,
        catalog?.currentLobby?.members,
        catalog?.currentLobbyCode,
        catalogUser?.userId,
        sendOwnerAction,
        user?.userId,
    ]);

    return (
        <Box
            sx={{
                background: colors.gradient.primary,
            }}
        >
            <GoogleOAuthProvider clientId="1068129342176-704fo06p1eqtqp9me1o948btj290qsf5.apps.googleusercontent.com">
                <LobbyReviewPanelBridgeProvider>
                    {twoFactorPending ? (
                        <TwoFactorVerify
                            twoFactorToken={state.twoFactorToken}
                            onSuccess={completeTwoFactor}
                            onCancel={cancelTwoFactor}
                        />
                    ) : !isPublicShellRoute ? (
                        <TopBar
                            isAuthenticated={isAuthenticated}
                            autoLoginInProgress={autoLoginInProgress}
                            googleResponse={googleResponse}
                            actions={actions}
                            onFailure={onFailure}
                            user={catalogUser || user}
                            catalog={catalog}
                            logout={logout}
                        />
                    ) : null}
                    <Helmet>
                        <html lang="en" />
                        <meta charSet="utf-8" />
                        <title>{publicPageMeta.title}</title>
                        <meta
                            name="description"
                            content={publicPageMeta.description || baseDescription}
                        />
                        <link
                            rel="canonical"
                            href={`${CANONICAL_BASE_URL}${pathname === "/" ? "" : pathname}`}
                        />

                        <meta property="og:type" content="website" />
                        <meta property="og:site_name" content="Ouro" />
                        <meta property="og:title" content={publicPageMeta.title} />
                        <meta
                            property="og:description"
                            content={publicPageMeta.description || baseDescription}
                        />
                        <meta
                            property="og:url"
                            content={`${CANONICAL_BASE_URL}${pathname === "/" ? "" : pathname}`}
                        />
                        <meta property="og:image" content={OG_IMAGE_URL} />
                        <meta property="og:image:width" content="1200" />
                        <meta property="og:image:height" content="630" />
                        <meta property="og:image:alt" content="Ouro — CS2 Matchmaking" />

                        <meta name="twitter:card" content="summary_large_image" />
                        <meta name="twitter:title" content={publicPageMeta.title} />
                        <meta
                            name="twitter:description"
                            content={publicPageMeta.description || baseDescription}
                        />
                        <meta name="twitter:image" content={OG_IMAGE_URL} />
                    </Helmet>

                    {isPublicShellRoute ? null : (
                        <Box sx={{ height: APP_SHELL_TOP_OFFSET_SX }} />
                    )}

                    {showRefragPromoLegacy ? (
                        <Box sx={{ position: "relative", zIndex: 1 }}>
                            <RefragPromoNotice dismissible />
                        </Box>
                    ) : null}

                    <AuthStateContext.Provider value={authContextValue}>
                        <UiDesignLaneGuard actions={actions} />
                        {!isPublicShellRoute ? (
                            <Box sx={{
                                width: "100%",
                                maxWidth: "100%",
                                pb: isMobileNav
                                    ? MOBILE_NAV_BOTTOM_INSET
                                    : { xs: "1.5rem", xl: "2rem" },
                                display: "flex",
                                flexDirection: "column",
                            }}>
                                <PageContainer sx={{ position: "relative" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: isMobileNav ? 0 : "1.5rem",
                                            alignItems: "stretch",
                                            minHeight: MAIN_SECTION_MIN_HEIGHT_SX,
                                        }}
                                    >
                                        <NavSidebar
                                            actions={actions}
                                            catalog={catalog}
                                            globalSearchOpen={globalPlayerSearchOpen}
                                            onGlobalSearchOpen={() => setGlobalPlayerSearchOpen(true)}
                                            onGlobalSearchClose={() => setGlobalPlayerSearchOpen(false)}
                                        />
                                        <Box
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                alignSelf: "stretch",
                                                display: "flex",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <MainSectionShell>
                                                {children}
                                            </MainSectionShell>
                                        </Box>
                                    </Box>
                                </PageContainer>
                                <GlobalPlayerSearch
                                    open={globalPlayerSearchOpen}
                                    onOpen={() => setGlobalPlayerSearchOpen(true)}
                                    onClose={() => setGlobalPlayerSearchOpen(false)}
                                    actions={actions}
                                    catalog={catalog}
                                />
                            </Box>
                        ) : (
                            <PublicTemplateShell
                                headerProps={{
                                    isAuthenticated,
                                    googleResponse,
                                    onFailure,
                                }}
                                footer={<PublicFooter />}
                                initialTemplate={catalog?.publicTemplate}
                                spacer={
                                    <Box
                                        sx={{
                                            height: {
                                                xs: "calc(68px + 1.5rem)",
                                                xl: "calc(78px + 1.5rem)",
                                            },
                                        }}
                                    />
                                }
                            >
                                <Box sx={{ minHeight: "100vh" }}>
                                    {children}
                                </Box>
                            </PublicTemplateShell>
                        )}
                    </AuthStateContext.Provider>

                    {!isPublicShellRoute ? (
                        <Box
                            id={LOBBY_REVIEW_PANEL_ANCHOR_ID}
                            sx={{
                                position: "fixed",
                                right: { xs: "0.75rem", xl: "1rem" },
                                bottom: isMobileNav
                                    ? MOBILE_NAV_FAB_BOTTOM
                                    : { xs: "0.75rem", xl: "1rem" },
                                zIndex: 1200,
                                pointerEvents: "none",
                            }}
                        />
                    ) : null}

                    <AnimatePresence initial={false}>
                        {showPersistentQueue ? (
                            <MotionBox
                                key="persistent-queue"
                                initial={{ opacity: 1, y: 0, scale: 1, x: "-50%" }}
                                animate={{ opacity: 1, y: 0, scale: 1, x: "-50%" }}
                                exit={{
                                    opacity: 0,
                                    y: 96,
                                    scale: 0.92,
                                    filter: "blur(6px)",
                                    x: "-50%",
                                }}
                                transition={{
                                    duration: 0.32,
                                    ease: [0.4, 0, 1, 1],
                                }}
                                sx={{
                                    position: "fixed",
                                    left: {
                                        xs: "50%",
                                        md: "calc(50% + 46px)",
                                        xl: "calc(50% + 51px)",
                                    },
                                    bottom: isMobileNav
                                        ? MOBILE_NAV_FAB_BOTTOM
                                        : { xs: "1.25rem", xl: "1.5rem" },
                                    zIndex: 1600,
                                    pointerEvents: "auto",
                                }}
                            >
                                <PersistentQueueStatus
                                    catalog={catalog}
                                    onCancel={handleCancelPersistentQueue}
                                />
                            </MotionBox>
                        ) : null}
                        {showPersistentMapVote ? (
                            <MotionBox
                                key="persistent-map-vote"
                                initial={{ opacity: 1, y: 0, scale: 1, x: "-50%" }}
                                animate={{ opacity: 1, y: 0, scale: 1, x: "-50%" }}
                                exit={{
                                    opacity: 0,
                                    y: 96,
                                    scale: 0.92,
                                    filter: "blur(6px)",
                                    x: "-50%",
                                }}
                                transition={{
                                    duration: 0.32,
                                    ease: [0.4, 0, 1, 1],
                                }}
                                sx={{
                                    position: "fixed",
                                    left: {
                                        xs: "50%",
                                        md: "calc(50% + 46px)",
                                        xl: "calc(50% + 51px)",
                                    },
                                    bottom: isMobileNav
                                        ? MOBILE_NAV_FAB_BOTTOM
                                        : { xs: "1.25rem", xl: "1.5rem" },
                                    zIndex: 1600,
                                    pointerEvents: "auto",
                                }}
                            >
                                <PersistentMapVoteStatus
                                    mapVoteState={mapVoteState}
                                />
                            </MotionBox>
                        ) : null}
                    </AnimatePresence>

                    <PlaySafeOnboardingDialog
                        open={showPlaySafeOnboarding}
                        onComplete={handlePlaySafeOnboardingComplete}
                        onDismiss={handlePlaySafeOnboardingDismiss}
                    />

                    <Toast />

                    <JoinPartyDialog
                        open={joinLobbyDialogOpened}
                        onCancel={handleCancelJoinLobbyDialog}
                        joinLobbyByCode={joinLobbyByCode}
                        lobbyCodeNotFound={lobbyCodeNotFound}
                        lobbyFull={lobbyFull}
                    />

                    {isAuthenticated && user?.userId ? (
                        <InviteDialog
                            open={Boolean(inviteDialogOpen && inviteData)}
                            inviteData={inviteData}
                            onDecline={handleInviteDeclined}
                            onAccept={handleInviteAccepted}
                            countdown={inviteCountdown}
                        />
                    ) : null}

                    {matchAcceptDialogState?.matchId &&
                    !isActiveLobbyPath ? (
                        <MatchReadyNotification
                            matchAcceptance={matchAcceptDialogState}
                            onAcceptMatch={onAcceptMatch}
                        />
                    ) : null}

                    {/* V2: vote lives in the lobby. Off /lobby-v2 a persistent
                        bar routes back; MapVoteDialog stays on the legacy shell. */}

                    {!isPublicShellRoute && isAuthenticated && user?.userId ? (
                        <>
                            <BackdropOverlay
                                mountWhenClosed
                                open={Boolean(catalog?.friendSidebarOpen)}
                                onClick={() => actions.setFriendSidebarOpen(false)}
                                zIndex={999}
                            />
                            <MotionBox
                                initial={{ x: "100%", opacity: 0 }}
                                animate={{
                                    x: catalog?.friendSidebarOpen ? 0 : "100%",
                                    opacity: catalog?.friendSidebarOpen ? 1 : 0,
                                }}
                                transition={RIGHT_PANEL_TRANSITION}
                                sx={{
                                    position: "fixed",
                                    top: 0,
                                    bottom: 0,
                                    right: 0,
                                    // Fixed desktop/tablet width — full bleed only on mobile nav
                                    width: "24rem",
                                    maxWidth: "24rem",
                                    [`@media ${MOBILE_NAV_MEDIA}`]: {
                                        width: "100%",
                                        maxWidth: "100%",
                                    },
                                    overflow: "hidden",
                                    boxSizing: "border-box",
                                    zIndex: 1401,
                                    display: "flex",
                                    flexDirection: "column",
                                    pointerEvents: catalog?.friendSidebarOpen ? "auto" : "none",
                                }}
                            >
                                <FriendlistSidebar
                                    setSidebarOpen={(open) => actions.setFriendSidebarOpen(open)}
                                    friendRequests={friendRequests}
                                    handleAcceptFriendRequest={handleAcceptFriendRequest}
                                    handleDeclineFriendRequest={handleDeclineFriendRequest}
                                    handleFriendRequestToCancel={handleFriendRequestToCancel}
                                    friends={friends}
                                    handleDeleteFriend={handleDeleteFriend}
                                    handleSendInvite={handleSendInvite}
                                    handleClearInviteError={handleClearInviteError}
                                    inviteState={inviteState}
                                    actions={actions}
                                    catalog={catalog}
                                    isLobbyFull={isLobbyFull}
                                />
                            </MotionBox>
                            <GlobalLobbySettingsDrawer
                                actions={actions}
                                catalog={catalog}
                            />
                            <ConfirmDialog
                                open={
                                    catalog?.confirmDialog &&
                                    catalog.confirmDialog.open &&
                                    catalog.confirmDialog.which === "deleteFriend"
                                }
                                IconComponent={Delete}
                                title={friendToDelete?.friendData?.nickname || "Friend"}
                                message={"Are you sure you want to remove this friend?"}
                                confirmText="Remove"
                                cancelText="Cancel"
                                onConfirm={handleConfirmDeleteFriend}
                                onCancel={handleCloseGlobalConfirmDialog}
                            />
                        </>
                    ) : null}

                    <CookieConsentBanner />
                </LobbyReviewPanelBridgeProvider>
            </GoogleOAuthProvider>
        </Box>
    );
}

App.propTypes = {
    children: PropTypes.node,
    actions: PropTypes.object.isRequired,
    catalog: PropTypes.object.isRequired,
};

export default App;
